import React from 'react';
import { ArrowUpRight, ExternalLink } from 'lucide-react';
import { WORK_ORDER_LABELS, workOrderSectionTestId } from '../../workOrderSections';
import './shared.css';

export interface BuildingCardProps {
    name: string;
    addressLines?: string[];
    /** The building's cover photo, shown as an 80px thumbnail */
    coverPhotoUrl?: string;
    /** Shown in place of a missing cover photo */
    placeholderUrl?: string;
    /** Opens in a new tab with rel=noopener */
    directionsUrl?: string;
    /** When given a round button opens the building page in a new tab */
    buildingHref?: string;
    /** A map under the building row (the admin modal passes a MiniMap) */
    map?: React.ReactNode;
}

/**
 * The building card: the cover thumbnail, name and address, a round link to
 * the building page and the directions button, with the map beside them
 * once the card is wide enough (under them when narrow).
 */
export const BuildingCard: React.FC<BuildingCardProps> = ({
    name,
    addressLines,
    coverPhotoUrl,
    placeholderUrl,
    directionsUrl,
    buildingHref,
    map
}) => {
    const lines = (addressLines ?? []).filter((line) => line.trim().length > 0);
    const thumbnail = coverPhotoUrl || placeholderUrl;

    return (
        <div className="wo-building wo-card" data-testid={workOrderSectionTestId('building')}>
            <div className={`wo-building__layout${map ? ' wo-building__layout--with-map' : ''}`}>
                <div className="wo-building__row">
                    {thumbnail && <img src={thumbnail} alt="" className="wo-building__thumb" />}
                    <div className="wo-building__info">
                        <div className="wo-building__name">{name}</div>
                        {lines.map((line, index) => (
                            <div key={index} className="wo-building__address">{line}</div>
                        ))}
                    </div>
                    {buildingHref && (
                        <a
                            href={buildingHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="wo-building__link-btn"
                            aria-label={`Öppna ${name}`}
                            title="Öppna byggnaden"
                        >
                            <ExternalLink size={16} aria-hidden="true" />
                        </a>
                    )}
                </div>
                {map && <div className="wo-building__map">{map}</div>}
                {directionsUrl && (
                    <a
                        href={directionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="wo-building__directions"
                    >
                        {WORK_ORDER_LABELS.directions}
                        <ArrowUpRight size={14} aria-hidden="true" />
                    </a>
                )}
            </div>
        </div>
    );
};

export default BuildingCard;
